import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { convertFromRaw, EditorState } from 'draft-js';
import { connect } from 'react-redux';
import { fetchAnswer, saveAnswer } from '../actions';
import {
  GECKO_CONSTS,
  GeckoField,
  GeckoFields,
  GeckoToastService,
  GeckoBtn,
  GeckoDialogService,
  GeckoIcon,
  GeckoLabel,
  OverlayTrigger,
  Tooltip,
} from 'ui';
import ReplyBoxField from 'components/ReplyBoxField';
import BlockField from 'components/BlockField';
import Loader from 'components/Loader';
import ElementRadio from 'components/ElementRadio';
import GeckoLocation from 'services/gecko-location';
import GeckoRequest from 'services/gecko-request';
import { renderError } from 'services/gecko-error';
import {
  buildContactGroup,
  buildSelectedChannelGroup,
} from 'services/template-tags';
import AsyncSelect from 'components/AsyncSelect';
import { UnsavedChangeble } from 'components/UnsavedChanges';
import SkillDataCapture from '../SkillDataCapture';
import SkillSuggestAnswer from '../SkillSuggestAnswer';
import SkillWorkflowAction from '../SkillWorkflowAction';
import SkillDisplayList from '../SkillDisplayList';
import SkillGeckoForm from '../SkillGeckoForm';
import BotTabEditContainer from '../BotTabEditContainer';
import NewSkillField from '../NewSkillField';
import ProgressRing from 'components/ProgressRing';
import { CONSTS } from 'constants/index.js';
import './index.scss';
import SkillPreview from '../SkillPreview';
import { BotTagManagerField } from '../BotTagManager';
import isDeepEqual from 'utilities/object-comparison';
import GeckoFeatures from 'services/gecko-features';
import AILoader from './components/AILoader';

const basicAnswerImage = `${process.env.PUBLIC_URL}/bot/basic-answer.gif`;

class QuestionBankEntry extends Component {
  static propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      value: this.props.value,
      clonedQuestion: this.props.clonedQuestion,
      duplicateQuestion: this.props.duplicateQuestion,
    };
    // This flag is added to fix an issue with replyBox component onChange being fired
    // too many times in a race condition on issue CHT-897.

    this.fieldChangeInProgress = false;
  }

  UNSAFE_componentWillReceiveProps(newProps, newState) {
    if (newProps.value !== this.state.value)
      this.setState({ value: newProps.value });
    if (newProps.duplicateQuestion !== this.state.duplicateQuestion)
      this.setState({ duplicateQuestion: newProps.duplicateQuestion });
    if (newProps.clonedQuestion !== this.state.clonedQuestion)
      this.setState({ clonedQuestion: newProps.clonedQuestion });
  }

  field = {
    key: 'value',
    type: GECKO_CONSTS.FIELD_TYPE_TEXT,
  };

  handler(_key, newValue) {
    this.setState({ value: newValue });
    this.props.onChange(newValue);
  }

  deleteQuestion() {
    this.props.onChange(null);
  }

  cloneQuestion() {
    this.props.copyQuestion(this.state.value);
  }

  getErrorState() {
    const errorStates = {
      duplicate: {
        message: 'Edit or delete to save answer',
        class: 'error',
        icon: 'far fa-times-circle',
      },
      clone: {
        message: 'Cloned Question',
        class: 'warning',
        icon: 'far fa-exclamation-triangle',
      },
    };
    if (this.state.duplicateQuestion === this.state.value)
      return errorStates['duplicate'];
    if (this.state.clonedQuestion === this.state.value)
      return errorStates['clone'];
    return null;
  }

  renderTooltipIcon() {
    const errorState = this.getErrorState();
    if (errorState)
      return (
        <span className="gecko-field-tooltip-icon">
          <OverlayTrigger
            placement="auto"
            overlay={
              <Tooltip id={`question-bank-${this.props.index}`}>
                {errorState.message}
              </Tooltip>
            }
          >
            <i id={`question-bank-${this.props.index}`}>
              <GeckoIcon icon="far fa-exclamation-triangle" />
            </i>
          </OverlayTrigger>
        </span>
      );
    if (this.props.isAISample) {
      return (
        <span className="gecko-field-tooltip-icon">
          <OverlayTrigger
            placement="auto"
            overlay={
              <Tooltip id={`question-bank-ai-${this.props.index}`}>
                Generated by ChatGPT
              </Tooltip>
            }
          >
            <i id={`question-bank-ai-${this.props.index}`}>
              <GeckoIcon icon="far fa-sparkles" />
            </i>
          </OverlayTrigger>
        </span>
      );
    }
  }

  render() {
    const errorState = this.getErrorState();
    const field = Object.assign({}, this.field, {
      className: errorState ? errorState.class : {},
    });
    return (
      <div className="QuestionBankEntry">
        <div>
          <GeckoField
            values={this.state}
            field={field}
            handler={this.handler.bind(this)}
          />
          {this.renderTooltipIcon()}
        </div>
        <OverlayTrigger
          placement="bottom"
          overlay={
            <Tooltip id={`clone-question-${this.props.index}`}>
              Clone Question
            </Tooltip>
          }
        >
          <button
            className="QuestionBankEntry-clone-button gecko-btn btn btn-outline-primary icon-only"
            id={`clone-question-${this.props.index}`}
            type="button"
            onClick={this.cloneQuestion.bind(this)}
          >
            <i className="far fa-copy"></i>
          </button>
        </OverlayTrigger>
        <OverlayTrigger
          placement="bottom"
          overlay={
            <Tooltip id={`delete-question-${this.props.index}`}>
              Delete Question
            </Tooltip>
          }
        >
          <button
            className="QuestionBankEntry-delete-button gecko-btn btn btn-outline-danger icon-only"
            id={`delete-question-${this.props.index}`}
            type="button"
            onClick={this.deleteQuestion.bind(this)}
          >
            <i className="far fa-trash-alt"></i>
          </button>
        </OverlayTrigger>
      </div>
    );
  }
}

class QuestionBank extends Component {
  static propTypes = {
    value: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      showCount: 10,
      duplicateQuestions: this.props.duplicateQuestions,
    };
  }

  componentWillReceiveProps(newProps) {
    if (newProps.duplicateQuestions !== this.state.duplicateQuestions)
      this.setState({ duplicateQuestions: newProps.duplicateQuestions });
  }

  handleChange(changedIndex) {
    return (newValue) => {
      if (newValue === null) {
        const newQuestions = this.props.value.filter(
          (_value, index) => changedIndex !== index
        );
        return this.props.onChange(newQuestions);
      } else {
        const newQuestions = this.props.value.map((value, index) => {
          if (changedIndex !== index) {
            return value;
          }
          return newValue;
        });
        return this.props.onChange(newQuestions);
      }
    };
  }

  showMore() {
    this.setState({ showCount: this.state.showCount + 50 });
  }

  copyQuestion(newSample) {
    if (newSample) {
      const newQuestions = Array.from(this.props.value);
      const changeIndex = newQuestions.lastIndexOf(newSample) + 1;
      newQuestions.splice(changeIndex, 0, newSample);
      if (changeIndex >= this.state.showCount || !changeIndex) this.showMore();
      this.props.onChange(newQuestions);
    }
  }

  getDuplicateQuestion(question) {
    if (!question) return null;
    else if (this.state.duplicateQuestions)
      return this.state.duplicateQuestions.find((ans) => ans === question);
    return null;
  }

  getClonedQuestion(question) {
    if (!question) return null;
    else if (
      this.props.value.indexOf(question) !==
      this.props.value.lastIndexOf(question)
    )
      return question;
    return null;
  }

  render() {
    const showing = this.props.value.slice(0, this.state.showCount);
    return (
      <div className="QuestionBank">
        {showing.map((value, index) => {
          const duplicateQuestion = this.getDuplicateQuestion(value);
          const clonedQuestion = this.getClonedQuestion(value);
          return (
            <div className="QuestionBank-sample" key={index}>
              <div className="QuestionBank-sample-number">Q{index + 1}</div>
              <QuestionBankEntry
                index={index}
                value={value}
                onChange={this.handleChange(index)}
                duplicateQuestion={duplicateQuestion}
                clonedQuestion={clonedQuestion}
                copyQuestion={this.copyQuestion.bind(this)}
                isAISample={this.props.aiSamples.includes(value)}
              />
            </div>
          );
        })}
        {this.props.value.length > this.state.showCount ? (
          <div className="QuestionBank-show-more">
            <div className="QuestionBank-show-more-count">
              Showing {this.state.showCount} of {this.props.value.length}{' '}
              Questions
            </div>
            <div className="QuestionBank-show-more-button">
              <GeckoBtn
                name="Show more questions"
                handler={this.showMore.bind(this)}
              />
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}

class QuestionAdder extends Component {
  static propTypes = {
    value: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func,
  };

  static defaultProps = {
    value: [],
    onChange: () => {},
  };

  constructor(props) {
    super(props);
    this.props = props;
    this.state = {
      newSample: '',
    };
  }

  inputField = {
    key: 'newSample',
    type: GECKO_CONSTS.FIELD_TYPE_TEXT,
  };

  changeHandler(_key, value) {
    this.setState({ newSample: value });
  }

  addSample(event) {
    if (event) {
      event.preventDefault();
    }
    if (!this.state.newSample) {
      return;
    }
    this.props.onChange(this.props.value.concat(this.state.newSample));
    this.setState({ newSample: '' });
  }

  render() {
    return (
      <div className="Answer-QuestionAdder">
        <form onSubmit={this.addSample.bind(this)}>
          <GeckoField
            field={this.inputField}
            values={this.state}
            handler={this.changeHandler.bind(this)}
          />
          <GeckoBtn
            type="default"
            name="Add Question"
            disabledWhen={() => !this.state.newSample}
            handler={this.addSample.bind(this)}
          />
        </form>
      </div>
    );
  }
}

class TrainingLevel extends Component {
  static propTypes = {
    value: PropTypes.arrayOf(PropTypes.string),
  };

  static target = 10;

  render() {
    let trainedPercentage =
      (this.props.value.length / TrainingLevel.target) * 100;
    if (trainedPercentage > 100) {
      trainedPercentage = 100;
    }
    const shouldShowAIDetails =
      GeckoFeatures.can('ai-actions') &&
      trainedPercentage < 100 &&
      trainedPercentage > 0;
    return (
      <div
        className={`Answer-TrainingLevel ${
          trainedPercentage === 100 ? 'Answer-TrainingLevel-100' : ''
        }`}
      >
        <div className="Answer-TraningLevel-details-container">
          <div className="Answer-TrainingLevel-progress">
            <ProgressRing
              radius="21"
              stroke="3"
              progress={trainedPercentage}
              backgroundRingColor="#E2E8F0"
            />
          </div>
          <div>
            <div className="Answer-TrainingLevel-content">
              <div className="Answer-TrainingLevel-content-title">
                Recommended Number of Questions
              </div>
              {trainedPercentage === 100 ? (
                <div className="Answer-TrainingLevel-content-subtitle">
                  Your bot is {trainedPercentage}% trained on this answer, great
                  job!
                </div>
              ) : (
                <div className="Answer-TrainingLevel-content-subtitle">
                  You have set {this.props.value.length} question
                  {this.props.value.length !== 1 ? 's' : ''}. For optimal
                  results we recommend adding at least 10 variations of your
                  question.
                </div>
              )}
            </div>
            {shouldShowAIDetails && (
              <>
                <div className="Answer-TrainingLevel-ai">
                  <div className="Answer-TrainingLevel-ai-title">
                    <GeckoIcon icon="far fa-sparkles" /> ChatGPT Assisted
                    Questions <GeckoLabel name="Beta" type="primary" />
                  </div>
                  <div className="Answer-TrainingLevel-ai-subtitle">
                    You can now use our new ChatGPT integration to magically
                    generate some more questions for you. Click the button below
                    to try it out!
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        {shouldShowAIDetails && (
          <div className="Answer-TraningLevel-action-container">
            <GeckoBtn
              name="Generate Questions"
              icon="far fa-sparkles"
              type="primary"
              handler={this.props.fetchAISamples}
            />
          </div>
        )}
      </div>
    );
  }
}

class Answer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      cloning: this.props.cloning || false,
      fetching: false,
      answer: null,
      duplicateQuestions: null,
      skills: [],
      aiSamples: [],
    };
  }

  componentDidMount() {
    const extractSamples = (samples) => {
      if (!samples) {
        return [];
      }
      if (Array.isArray(this.props.params.samples)) {
        return samples;
      }
      return [samples];
    };
    if (this.props.answerId === 'new') {
      return this.setState(
        {
          answer: {
            id: 'new',
            type: 'answer',
            name: this.props.params.name || '',
            samples: extractSamples(this.props.params.samples),
            answers: [''],
            skill_id: null,
          },
        },
        this.props.clearUnsavedChanges
      );
    }
    if (!this.props.answer || this.props.cloning) {
      this.fetchAnswer();
    } else {
      this.setState(
        { answer: this.props.answer },
        !this.props.cloning ? this.props.clearUnsavedChanges : null
      );
    }
  }

  templateContext = [
    buildContactGroup(),
    buildSelectedChannelGroup({
      channel: null,
      showChannelQueuePosition: true,
    }),
  ];

  fetchAnswer() {
    this.setState({ fetching: true });
    return fetchAnswer(
      this.props.answerId,
      this.props.botId,
      this.props.cloning
    )
      .then(() => {
        this.setState(
          { fetching: false },
          !this.props.cloning ? this.props.clearUnsavedChanges : null
        );
      })
      .catch(() => {
        this.setState(
          { fetching: false },
          !this.props.cloning ? this.props.clearUnsavedChanges : null
        );
      });
  }

  handleNewSkillClick() {
    this.setState({
      addingNewSkill: true,
    });
  }

  handleEditSkillClick() {
    if (!this.state.answer.skill_id)
      new GeckoToastService().error('Skill cannot be found.');
    const usedSkill = this.state.skills.find(
      (skill) => skill.id === this.state.answer.skill_id
    );
    GeckoLocation.goTo(
      `/bots/${this.props.botId}/skills/${usedSkill.type}/${usedSkill.id}?answer_id=${this.state.answer.id}`
    );
  }

  async fetchAISamples() {
    try {
      this.setState({ fetchingAISamples: true });
      const { samples } = this.state.answer;
      const numberOfQuestions = 10 - samples.length;
      const { questions } = await GeckoRequest.post('/bots/get-ai-samples', {
        questions: samples,
        numberOfQuestions,
      });
      const newSamples = [...this.state.answer.samples, ...questions];
      this.setState({
        answer: { ...this.state.answer, samples: newSamples },
        aiSamples: [...this.state.aiSamples, ...questions],
      });
    } catch (err) {
      new GeckoToastService().error('Failed to fetch AI Samples');
    } finally {
      this.setState({ fetchingAISamples: false });
    }
  }

  titleFields() {
    return [
      {
        key: 'name',
        label: 'Answer Title',
        required: true,
        description:
          'The title of your answer will not be visible to the student, they will see one of the sample questions if we are verifying their intent',
        type: GECKO_CONSTS.FIELD_TYPE_TEXT,
        width: '100',
      },
    ];
  }

  botTagFields() {
    return [
      {
        key: 'bot_tag_ids',
        label: 'Answer Tags',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        width: '100%',
        component: <BotTagManagerField item={this.state.answer} />,
      },
    ];
  }

  questionAddingFields() {
    return [
      {
        key: '',
        label: 'Questions',
        description:
          'We recommend at least 10 variations on a question to allow us to predict the intent of the message more accurately.',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        width: '100%',
        className: 'BotTabEditContainer-section-heading',
        component: <div style={{ display: 'none' }}></div>,
      },
      {
        key: 'samples',
        label: 'Ask a Question',
        required: !this.state.answer.samples.length,
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        width: '100%',
        component: <QuestionAdder />,
      },
      {
        key: 'samples',
        label: 'Question Bank',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        hideWhen: () =>
          !this.state.answer.samples || !this.state.answer.samples.length,
        width: '100',
        component: (
          <QuestionBank
            aiSamples={this.state.aiSamples}
            duplicateQuestions={this.state.duplicateQuestions}
          />
        ),
      },
      {
        key: 'samples',
        label: null,
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        width: '100%',
        component: (
          <TrainingLevel fetchAISamples={this.fetchAISamples.bind(this)} />
        ),
      },
    ];
  }

  onSkillOptionsUpdated(skillOptions) {
    const skills = skillOptions.map((skillOption) => {
      return {
        id: skillOption.value,
        name: skillOption.label,
        type: skillOption.type,
        internal_skill: skillOption.internal_skill,
      };
    });
    this.setState({ skills });
  }

  answerFields() {
    return [
      {
        key: '',
        label: 'Answers',
        description: 'Create or choose how to reply to the questions above',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        width: '100%',
        className: 'BotTabEditContainer-section-heading',
        component: <div style={{ display: 'none' }}></div>,
      },
      {
        key: 'type',
        label: 'Respond With',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        width: '100%',
        component: (
          <ElementRadio
            options={[
              {
                component: <button>Message</button>,
                value: 'answer',
              },
              {
                component: <button>Skill</button>,
                value: 'skill',
              },
            ]}
          />
        ),
      },
      {
        key: 'skill_id',
        label: 'Choose an Existing Skill',
        required: true,
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        hideWhen: () =>
          !this.state.answer ||
          !this.state.answer.type ||
          this.state.answer.type !== 'skill' ||
          this.state.addingNewSkill,
        width: '100%',
        component: (
          <AsyncSelect
            selectUrl={`/bots/${this.props.botId}/skills/select`}
            getUrl={`/bots/${this.props.botId}/skills`}
            field={{
              config: {
                valueRenderer: this.renderSkillRow.bind(this),
                optionRenderer: this.renderSkillRow.bind(this),
              },
            }}
            optionTransform={(skill) => ({
              label: skill.name,
              value: skill.id,
              type: skill.internal_skill,
            })}
            onOptionsUpdated={this.onSkillOptionsUpdated.bind(this)}
          />
        ),
        secondaryDescription: (
          <div className="Answer-create-skill-text">
            <span
              className="Answer-create-skill-text-link"
              onClick={this.handleNewSkillClick.bind(this)}
            >
              Create a new skill{' '}
            </span>
            {this.state.answer.skill_id ? (
              <div>
                or{' '}
                <span
                  className="Answer-create-skill-text-link"
                  onClick={this.handleEditSkillClick.bind(this)}
                >
                  edit this skill
                </span>
              </div>
            ) : (
              ''
            )}
          </div>
        ),
      },
      {
        key: 'new_skill_type',
        label: 'Choose Skill Type',
        hideWhen: () =>
          !this.state.answer ||
          !this.state.answer.type ||
          this.state.answer.type !== 'skill' ||
          !this.state.addingNewSkill ||
          this.state.answer.new_skill_type,
        width: '100%',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        component: <NewSkillField />,
      },
      {
        key: 'answers',
        label: 'Compose Answer',
        required: true,
        hideWhen: () =>
          !this.state.answer ||
          !this.state.answer.type ||
          this.state.answer.type !== 'answer',
        width: '100%',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        component: (
          <BlockField
            addButton={{
              type: 'default',
              name: 'Add Another Message',
              icon: 'far fa-plus',
            }}
            field={{
              label: null,
              type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
              component: (
                <ReplyBoxField
                  formatAsEditorObject
                  editorMode={CONSTS.EDITOR_MODE_TYPE.RICH}
                  compileTemplateTags={false}
                  templateContext={this.templateContext}
                  meta={{ mail_merge: { show: true } }}
                />
              ),
            }}
          />
        ),
      },
    ];
  }

  renderSkillRow(skill) {
    const skillData = CONSTS.SKILL_TYPES[skill.type];
    return (
      <div className="col-12 row answer-select">
        <span className="col-7">{skill.label}</span>
        <span className="col-5">
          <i
            className={skillData ? skillData.icon : 'far fa-clipboard-check'}
          ></i>{' '}
          {skillData.name}
        </span>
      </div>
    );
  }

  renderSkillFields() {
    if (!this.state.answer.new_skill_type) {
      return null;
    }

    const changeHandler = (value) => {
      this.handleFieldChange('skill', value);
    };

    if (this.state.answer.new_skill_type === 'data_capture') {
      return (
        <SkillDataCapture
          botId={this.props.botId}
          value={this.state.answer.skill}
          onChange={changeHandler}
        />
      );
    }
    if (this.state.answer.new_skill_type === 'workflow_action') {
      return (
        <SkillWorkflowAction
          botId={this.props.botId}
          value={this.state.answer.skill}
          onChange={changeHandler}
        />
      );
    }
    if (this.state.answer.new_skill_type === 'suggest_answer') {
      return (
        <SkillSuggestAnswer
          botId={this.props.botId}
          value={this.state.answer.skill}
          onChange={changeHandler}
        />
      );
    }
    if (this.state.answer.new_skill_type === 'display_list') {
      return (
        <SkillDisplayList
          botId={this.props.botId}
          value={this.state.answer.skill}
          onChange={changeHandler}
        />
      );
    }
    if (this.state.answer.new_skill_type === 'gecko_form') {
      return (
        <SkillGeckoForm
          botId={this.props.botId}
          value={this.state.answer.skill}
          onChange={changeHandler}
        />
      );
    }
  }

  skillFields() {
    return [
      {
        key: 'skill_id',
        label: 'Choose a Skill',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT,
        width: '25%',
        options: this.props.skills.map((skill) => {
          return {
            label: skill.name,
            value: skill.id,
          };
        }),
      },
      {
        key: 'new_skill_type',
        label: 'Create a New Skill',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT,
        width: '25%',
        options: [
          {
            label: 'Workflow Action',
            value: 'HANDOVER',
          },
          {
            label: 'Suggest Answer',
            value: 'SUGGEST_ANSWER',
          },
          {
            label: 'Display List',
            value: 'DISPLAY_LIST',
          },
          {
            label: 'Gecko Form',
            value: 'GECKO_FORM',
          },
        ],
      },
    ];
  }

  newSkillFields() {
    return [
      {
        key: 'skill',
        label: 'Data capture skill',
        hideWhen: () => this.state.answer.new_skill_type !== 'DATA_CAPTURE',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        component: (
          <SkillDataCapture
            botId={this.props.botId}
            isNew={true}
            onSave={(skill) => {
              const answer = Object.assign({}, this.state.answer, {
                skill: null,
                new_skill_type: null,
                skill_id: skill.id,
              });
              this.setState({ answer });
            }}
          />
        ),
      },
      {
        key: 'skill',
        hideWhen: () => this.state.answer.new_skill_type !== 'HANDOVER',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        component: <SkillWorkflowAction botId={this.props.botId} />,
      },
      {
        key: 'skill',
        hideWhen: () => this.state.answer.new_skill_type !== 'SUGGEST_ANSWER',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        component: <SkillSuggestAnswer botId={this.props.botId} />,
      },
      {
        key: 'skill',
        hideWhen: () => this.state.answer.new_skill_type !== 'DISPLAY_LIST',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        component: <SkillDisplayList botId={this.props.botId} />,
      },
      {
        key: 'skill',
        hideWhen: () => this.state.answer.new_skill_type !== 'GECKO_FORM',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        component: <SkillGeckoForm botId={this.props.botId} />,
      },
    ];
  }

  componentDidUpdate(lastProps) {
    if (JSON.stringify(this.props.answer) !== JSON.stringify(lastProps.answer))
      this.setState({ answer: this.props.answer });
    if (this.props.cloning !== lastProps.cloning) {
      this.setState({ cloning: this.props.cloning });
      this.fetchAnswer();
    }
  }

  handleFieldChange(key, value) {
    if (this.fieldChangeInProgress) {
      return;
    }
    this.fieldChangeInProgress = true;
    const newAnswer = Object.assign({}, this.state.answer, { [key]: value });
    if (key === 'type') {
      this.setState({ addingNewSkill: false });
      delete newAnswer.skill;
      delete newAnswer.new_skill_type;
      newAnswer.skill_id = null;
    }
    if (key === 'new_skill_type' && value) {
      delete newAnswer.skill_id;
    }
    return this.setState({ answer: newAnswer }, () => {
      this.fieldChangeInProgress = false;
    });
  }

  saveAnswer() {
    const answers = this.state.answer.answers.reduce((answers, answer) => {
      const content = EditorState.createWithContent(convertFromRaw(answer))
        .getCurrentContent()
        .getPlainText();
      const editor_object = answer;
      return answers.concat({ content, editor_object });
    }, []);

    const answer = {
      answers,
      id: this.state.answer.id,
      name: this.state.answer.name,
      type: this.state.answer.type,
      samples: this.state.answer.samples,
      skill_id: this.state.answer.skill_id,
      skill: this.state.answer.skill,
      miss_ids: this.props.params.misses || null,
      bot_tag_ids: this.state.answer.bot_tag_ids,
    };

    return saveAnswer(answer, this.props.botId)
      .then((answer) => {
        this.props.clearUnsavedChanges();
        new GeckoToastService().success('Answer successfully saved');
        GeckoLocation.goTo(`/bots/${this.props.botId}/answers`);
      })
      .catch((renderError) => {
        renderError.safe.forEach((error) =>
          new GeckoToastService().error(error)
        );
        if (renderError.additional) {
          this.setState({
            duplicateQuestions: renderError.additional.duplicateResponses,
          });
        }
        // removes duplicates if there are any
        const answer = this.state.answer;
        answer.samples = this.state.answer.samples.filter(
          (sample, index) => this.state.answer.samples.indexOf(sample) === index
        );
        this.setState({ answer });
      });
  }

  deleteAnswer() {
    return new GeckoDialogService()
      .confirm(`Are you sure you want to delete this answer?`)
      .then((response) => {
        if (response) {
          return GeckoRequest.delete(
            `/bots/${this.props.botId}/answers/${this.state.answer.id}`
          )
            .then(() => {
              this.props.clearUnsavedChanges();
              new GeckoToastService().success('Answer deleted');
              return GeckoLocation.goTo(`/bots/${this.props.botId}/answers`);
            })
            .catch(renderError);
        }
        return false;
      })
      .catch(renderError);
  }

  handleCancel() {
    this.props.clearUnsavedChanges();
    return GeckoLocation.goTo(`/bots/${this.props.botId}/answers`);
  }

  handleClone() {
    return GeckoLocation.goTo(
      `/bots/${this.props.botId}/answers/clone/${this.props.answerId}`
    );
  }

  renderPreview() {
    if (
      this.state.answer.type === 'skill' &&
      this.state.answer.new_skill_type
    ) {
      return <SkillPreview skill_type={this.state.answer.new_skill_type} />;
    }
    if (this.state.answer.type === 'skill' && this.state.answer.skill_id) {
      const usedSkill = this.state.skills.find(
        (skill) => skill.id === this.state.answer.skill_id
      );
      return (
        <SkillPreview
          skill_type={usedSkill ? usedSkill.type.toLowerCase() : 'default'}
        />
      );
    }

    const basicAnswerPreview = {
      label: 'Answer type preview',
      image: basicAnswerImage,
    };

    const previewData = basicAnswerPreview;
    return (
      <div className="Answer-answer-preview col-12 col-lg-6 col-xl-5">
        <label>{previewData.label}</label>
        <img
          src={
            previewData.image + `?gif-cache-busting=${this.state.gifRandomKey}`
          }
        />
      </div>
    );
  }

  render() {
    if (!this.state.answer) {
      return null;
    }

    return (
      <div className="Answer">
        <Loader isLoading={this.state.fetching}>
          <AILoader
            isLoading={this.state.fetchingAISamples}
            loadingText="Generating Questions"
          >
            <BotTabEditContainer
              title={
                this.state.answer.id === 'new'
                  ? 'Add a New Answer'
                  : 'Update Answer'
              }
              updated={{
                by:
                  this.state.answer.last_updated_by &&
                  this.props.agents.find(
                    (agent) => agent.id === this.state.answer.last_updated_by
                  ),
                at: this.state.answer.updatedAt,
              }}
              saveText="Save Answer"
              onSave={this.saveAnswer.bind(this)}
              onCancel={this.handleCancel.bind(this)}
              onDelete={
                !this.props.cloning && this.state.answer.id !== 'new'
                  ? this.deleteAnswer.bind(this)
                  : null
              }
              onClone={
                !this.props.cloning && this.state.answer.id !== 'new'
                  ? this.handleClone.bind(this)
                  : null
              }
              disableSave={isDeepEqual(this.props.answer, this.state.answer)}
            >
              <div className="Answer-container Answer-details col-12">
                <div>
                  <GeckoFields
                    fields={this.titleFields().concat(this.botTagFields())}
                    values={this.state.answer}
                    handler={this.handleFieldChange.bind(this)}
                  />
                </div>
              </div>
              <div className="Answer-container Questions col-12">
                <div>
                  <GeckoFields
                    fields={this.questionAddingFields()}
                    values={this.state.answer}
                    handler={this.handleFieldChange.bind(this)}
                  />
                </div>
              </div>
              <div className="Answer-container Answers col-12">
                <div>
                  <div className="Answer-answer-content">
                    <GeckoFields
                      fields={this.answerFields()}
                      values={this.state.answer}
                      handler={this.handleFieldChange.bind(this)}
                    />
                    {this.renderSkillFields()}
                  </div>
                </div>
              </div>
            </BotTabEditContainer>
          </AILoader>
        </Loader>
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  const getAnswer = () => {
    const answer = state.botsRoute.answersById[state.location.slug];

    if (!answer) {
      return null;
    }

    return {
      ...answer,
      answers: answer.answers.reduce((answers, answer) => {
        if (!answer) {
          return answers;
        }
        return answers.concat(answer.editor_object);
      }, []),
    };
  };

  return {
    cloning: props.cloning,
    params: state.location.params,
    answerId: state.location.slug,
    answer: getAnswer(),
    skills: state.skills.filter((skill) => skill.bot_id === props.botId),
    agents: state.agents,
  };
};

export default connect(mapStateToProps)(UnsavedChangeble(['answer'])(Answer));
